import { createLogic } from 'redux-logic';
import { IEvaReduxStoreCurrentUserShape } from '../state';
import { createServiceRequestLogic } from './request';
import { Core } from '@springtree/eva-services-core';
import { createCurrentUserTokenAction, createServiceRequestAction, createServiceResetAction, IEvaCurrentUserTokenAction } from '../action';
import { getServicePromise } from '../promise';

/**
 * Creates all the EVA current user logic
 *
 * @export
 * @template SVC
 * @param {new () => SVC} serviceDefinition The EVA Service registry definition
 * @param {string} [stateKey] Optional state key name if not the same as the service name
 * @returns {Logic<IEvaServiceState<SVC>, any, any, any, any, string>}
 */
export function createCurrentUserLogic<SHAPE extends IEvaReduxStoreCurrentUserShape>(
) {

  // Create the service request logics
  // These will handle the effect of actually calling the EVA services
  //
  const loginLogic = createServiceRequestLogic(Core.Login, { stateKey: 'login' });
  const logoutLogic = createServiceRequestLogic(Core.Logout, { stateKey: 'logout' });
  const currentUserLogic = createServiceRequestLogic(Core.GetCurrentUser, { stateKey: 'currentUser' });
  const registerLogic = createServiceRequestLogic(Core.CreateCustomer, { stateKey: 'register' });

  // Setting the user token will trigger a current user fetch
  // Do no remove successType here. It somehow breaks the flow :(
  //
  const setTokenUpdateUserLogic = createLogic<SHAPE, IEvaReduxStoreCurrentUserShape, any, any, any, string>({
    type: 'CURRENT_USER_TOKEN',
    processOptions: {
      dispatchReturn: true,
      successType: 'currentUser:REQUEST',
    },
    async process({ action }) {
      // If a new token is set we need to trigger a fetch for the current user data
      // There is no payload and the current Authorization token header will be set
      // from the current state
      //
      const payload = (action as IEvaCurrentUserTokenAction).payload;
      const currentUserAction = createServiceRequestAction({
        service: Core.GetCurrentUser,
        chainContext: payload.chainContext,
      });

      // Push to the end of the call stack to let the reducers finish
      // and any related chains start
      //
      const callPromise = getServicePromise(payload.serviceContext as string);
      if (callPromise) {
        setTimeout(() => callPromise.resolve(payload.token || ''), 0);
      }

      return currentUserAction.payload;
    },
  });

  // A successful login will trigger a set token action which in turn will
  // trigger the current user fetch
  //
  const loginUpdateUserLogic = createLogic<SHAPE, IEvaReduxStoreCurrentUserShape, any, any, any, string>({
    type: 'login:RESPONSE',
    processOptions: {
      dispatchReturn: true,
    },
    async process({ getState, action }) {
      // Check if the login response was a success
      //
      const storeState: IEvaReduxStoreCurrentUserShape = getState();
      const loginState = storeState.login;
      if (
        loginState?.data?.response?.User?.AuthenticationToken &&
        loginState.data.response.Authentication === EVA.Core.AuthenticationResults.Authenticated
      ) {
        const setTokenAction = createCurrentUserTokenAction({
          token: loginState.data.response.User.AuthenticationToken,
          chainContext: action.payload.chainContext,
        });
        return setTokenAction;
      }

      // No dispatch needed
      //
      return;
    },
  });

  // A successful or failed logout will trigger a set token action which in turn will
  // trigger the current user fetch
  // To token will be set to undefined
  //
  const logoutUpdateUserLogic = createLogic<SHAPE, IEvaReduxStoreCurrentUserShape, any, any, any, string>({
    type: ['logout:RESPONSE', 'logout:ERROR'],
    processOptions: {
      dispatchReturn: true,
    },
    async process({ action }) {
      // Always dispatch
      //
      const unsetTokenAction = createCurrentUserTokenAction({
        chainContext: action.payload.chainContext,
      });
      return unsetTokenAction;
    },
  });

  // Registering a customer with a returned Authentication token will trigger
  // a current user fetch
  //
  const registerAutoLoginLogic = createLogic<SHAPE, IEvaReduxStoreCurrentUserShape, any, any, any, string>({
    type: 'register:RESPONSE',
    processOptions: {
      dispatchReturn: true,
    },
    async process({ getState, action }) {
      // Check if the register response contains a token
      //
      const storeState: IEvaReduxStoreCurrentUserShape = getState();
      const registerState = storeState.register;
      if (
        registerState &&
        registerState.data &&
        registerState.data.response &&
        registerState.data.response.User &&
        registerState.data.response.User.AuthenticationToken
      ) {
        const setTokenAction = createCurrentUserTokenAction({
          token: registerState.data.response.User.AuthenticationToken,
          chainContext: action.payload.chainContext,
        });
        return setTokenAction;
      }

      // No dispatch needed
      //
      return;
    },
  });

  return [
    ...loginLogic,
    ...logoutLogic,
    ...currentUserLogic,
    ...registerLogic,
    setTokenUpdateUserLogic,
    loginUpdateUserLogic,
    logoutUpdateUserLogic,
    registerAutoLoginLogic,
  ];
}
